import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, Stack, Typography, IconButton } from '@mui/material';
import { uploadBrochure, uploadCover, uploadImagesBulk, Hotel, deleteImage, reorderImages } from '../../api/hotels.ts';
import http from '../../api/http.ts';
import DeleteIcon from '@mui/icons-material/Delete';
import { toastError, toastSuccess } from '../../shared/ui/toast.ts';

const HotelMediaTab: React.FC<{ hotelId: number; model: Partial<Hotel>; setModel: (m: any)=>void }> = ({ hotelId, model, setModel }) => {
  const [bulkPreview, setBulkPreview] = useState<string[]>([]);

  const onUploadCover = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0]; if (!f) return;
    try {
      const url = await uploadCover(hotelId, f);
      setModel({ ...model, coverImageUrl: url });
      toastSuccess('Cover uploaded');
    } catch (err:any) {
      const msg = err?.response?.data?.error || 'Upload failed';
      toastError(msg);
    }
  };

  const onUploadPdf = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0]; if (!f) return;
    try {
      const url = await uploadBrochure(hotelId, f);
      setModel({ ...model, brochurePdfUrl: url });
      toastSuccess('PDF uploaded');
    } catch (err:any) {
      const msg = err?.response?.data?.error || 'Upload failed';
      toastError(msg);
    }
  };

  const onUploadBulk = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setBulkPreview(files.map(f => URL.createObjectURL(f)));
    try {
      await uploadImagesBulk(hotelId, files);
      toastSuccess('Images uploaded');
    } catch (err:any) {
      const msg = err?.response?.data?.error || 'Upload failed';
      toastError(msg);
    }
  };

  // Placeholder gallery state (replace with fetch existing images)
  const [gallery, setGallery] = useState<{ id:number; url:string; sortOrder:number }[]>([]);
  useEffect(() => {
    http.get(`/api/v1/admin/hotels/${hotelId}/images`)
      .then((arr:any[]) => {
        setGallery((arr||[]).map((x:any, i:number) => ({ id: x.id, url: x.url, sortOrder: x.sortOrder ?? i })));
      }).catch(()=>{});
  }, [hotelId]);
  const sorted = useMemo(()=>[...gallery].sort((a,b)=> (a.sortOrder??0) - (b.sortOrder??0)),[gallery]);

  const onDelete = async (imageId:number) => {
    try { await deleteImage(hotelId, imageId); setGallery(g => g.filter(x=>x.id!==imageId)); }
    catch(err:any){ const msg = err?.response?.data?.error || 'Delete failed'; toastError(msg); }
  };

  const onSaveOrder = async () => {
    try { await reorderImages(hotelId, sorted.map((x,i)=>({ id:x.id, sortOrder:i }))); toastSuccess('Order saved'); }
    catch(err:any){ const msg = err?.response?.data?.error || 'Reorder failed'; toastError(msg); }
  };

  return (
    <Box mt={2}>
      <Typography variant="subtitle1" gutterBottom>Cover image</Typography>
      <Stack direction="row" spacing={2} alignItems="center">
        <Button variant="outlined" component="label">Upload JPG/PNG<input hidden accept="image/png,image/jpeg" type="file" onChange={onUploadCover} /></Button>
        {model.coverImageUrl && <a href={model.coverImageUrl} target="_blank" rel="noreferrer">View cover</a>}
      </Stack>

      <Typography variant="subtitle1" gutterBottom mt={3}>Brochure PDF</Typography>
      <Stack direction="row" spacing={2} alignItems="center">
        <Button variant="outlined" component="label">Upload PDF<input hidden accept="application/pdf" type="file" onChange={onUploadPdf} /></Button>
        {model.brochurePdfUrl && <a href={model.brochurePdfUrl} target="_blank" rel="noreferrer">View brochure</a>}
      </Stack>

      <Typography variant="subtitle1" gutterBottom mt={3}>Gallery images (bulk)</Typography>
      <Stack direction="row" spacing={2} alignItems="center">
        <Button variant="outlined" component="label">Upload multiple<input hidden accept="image/png,image/jpeg" type="file" multiple onChange={onUploadBulk} /></Button>
        <Button variant="contained" onClick={onSaveOrder}>Save order</Button>
      </Stack>
      <Stack direction="row" spacing={1} mt={2} flexWrap="wrap">
        {sorted.map((it) => (
          <Box key={it.id} sx={{ position:'relative' }}>
            <img src={it.url} alt="img" style={{ width: 96, height: 96, objectFit:'cover', borderRadius: 4 }} />
            <IconButton size="small" color="error" onClick={()=>onDelete(it.id)} sx={{ position:'absolute', top: -8, right: -8 }}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Box>
        ))}
      </Stack>
    </Box>
  );
};

export default HotelMediaTab;


